import { mountTerminal, type TerminalController } from "./terminal.js";
import { openWizard } from "./wizard.js";
import { setupScreenshotButton } from "./screenshot.js";
import { setupMarketing } from "./marketing.js";
import { setupWatchdog } from "./marketing-watchdog.js";
import { setupWettbewerb } from "./marketing-wettbewerb.js";

interface MeResponse { username: string; email: string | null }
interface Project {
  id: number; slug: string; name: string; stagingUrl: string;
  uiTerminalPosition: "bottom" | "right";
}
interface ProjectsResponse { projects: Project[] }
interface Snapshot {
  id: number; project_id: number; type: string;
  label: string | null; description: string | null;
  created_at: string; keep_forever: number;
  git_commit_sha: string | null; sql_dump_path: string | null;
  size_bytes: number;
}
interface ProjectFullResponse {
  slug: string; name: string;
  stagingRootPath: string; stagingUrl: string;
  stagingDbName: string; stagingDbUser: string;
  stagingDbPassword?: string;
  stagingBasicAuthUser: string;
  stagingBasicAuthPassword?: string;
  liveUrl: string; liveSshHost: string; liveSshPort: number; liveSshUser: string;
  liveSshPrivateKey?: string;
  liveJoomlaPath: string;
  liveDbHost: string; liveDbPort: number; liveDbUser: string; liveDbName: string;
  liveDbPassword?: string;
  anthropicApiKey?: string;
  uiTerminalPosition: "bottom" | "right";
  rsyncExcludesJson: string; snapshotRetention: number;
}

// Stage-Abgleich: ein Portal-Eintrag aus GET /api/stage-drift. Auf Modulebene,
// weil sowohl die Drift-Fahne in initApp() als auch der Sammel-Pull-Dialog
// (openPullAllModal, unten) dieselbe Form brauchen.
interface DriftPortal {
  slug: string; pullNoetig: boolean; unbekannt?: boolean;
  dateien: number | null; artikel: number | null; aktionen: number | null;
  startseiteOk: boolean; letzterPull: string | null; hinweise: string[];
}

function driftText(d: DriftPortal): string {
  const teile: string[] = [];
  if (d.dateien) teile.push(`${d.dateien} Datei${d.dateien === 1 ? "" : "en"}`);
  if (d.artikel) teile.push(`${d.artikel} Artikel`);
  if (d.aktionen) teile.push(`${d.aktionen} Backend-Aktion${d.aktionen === 1 ? "" : "en"}`);
  return teile.join(", ");
}

// Socket-Kanal des Sammel-Pulls (siehe SAMMEL_KANAL in src/routes/pull-all.ts —
// dort nicht importierbar, das ist Server-Code; der Wert ist Teil der API).
const PULL_ALL_KANAL = "__pull-all__";

// Pull-Sperre je Projekt (Uli 30.09.2026, Server: src/sync/pull-sperre.ts): „Staging in Arbeit“ — der Server
// lehnt jeden Pull dieses Projekts ab (423). Die Oberfläche zeigt den Stand an Toolbar, Fahne, Umschalter und
// im Sammel-Pull und bietet Setzen/Lösen an. Modulweit, weil openPullAllModal() ihn außerhalb der Toolbar braucht.
type PullSperreInfo = { grund: string; seit: string };
let pullSperren: Record<string, PullSperreInfo> = {};
async function ladePullSperren(): Promise<void> {
  try {
    const r = await api<{ sperren?: Record<string, PullSperreInfo> }>("/api/pull-sperre");
    if (r.status === 200) pullSperren = r.data?.sperren ?? {};
  } catch { /* Netzfehler: letzter Stand bleibt; der Server setzt die Sperre ohnehin durch */ }
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]!) : null;
}

async function api<T>(
  url: string,
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE" = "GET",
  body?: unknown,
): Promise<{ status: number; data: T | null }> {
  const csrf = getCookie("csrf_token");
  const headers: Record<string, string> = {};
  // Only set content-type when there's actually a body — Fastify's default
  // JSON parser rejects empty bodies with content-type: application/json
  // (FST_ERR_CTP_EMPTY_JSON_BODY → 400), which broke DELETE requests with
  // no body (Reset-Button on the terminal panel).
  if (body !== undefined) headers["content-type"] = "application/json";
  if (csrf && method !== "GET") headers["x-csrf-token"] = csrf;
  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(url, init);
  let data: T | null = null;
  try { data = (await res.json()) as T; } catch { /* */ }
  return { status: res.status, data };
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function formatDate(iso: string): string { return new Date(iso).toLocaleString("de-DE"); }

async function openPullModal(): Promise<void> {
  const slug = currentSlug();
  if (!slug) return;
  const m = openModal("tpl-pull-modal");
  const log = m.querySelector("[data-log]") as HTMLElement;
  const fastCheckbox = m.querySelector('input[name="fast"]') as HTMLInputElement;
  const submit = m.querySelector("[data-submit]") as HTMLButtonElement;
  const testMsg = m.querySelector('[data-msg="test"]') as HTMLElement;
  let isDone = false;

  const append = (text: string, cls?: string) => {
    const span = document.createElement("span");
    if (cls) span.className = cls;
    span.textContent = text + "\n";
    log.appendChild(span);
    log.scrollTop = log.scrollHeight;
  };

  // Live counter — single line that updates in-place via textContent for
  // rsync-progress and db-progress stages. Beats appending hundreds of log
  // lines for each tick.
  const liveLine = document.createElement("div");
  liveLine.className = "stage-line live-counter";
  liveLine.style.fontWeight = "600";
  liveLine.style.padding = "0.3em 0";
  log.parentElement?.insertBefore(liveLine, log);

  // Heartbeat: warn if no event in 30s. Resets on every event.
  const heartbeatEl = document.createElement("div");
  heartbeatEl.className = "stage-line";
  heartbeatEl.style.color = "#f59e0b";
  heartbeatEl.style.padding = "0.2em 0";
  heartbeatEl.hidden = true;
  log.parentElement?.insertBefore(heartbeatEl, log);
  let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  const resetHeartbeat = (): void => {
    if (heartbeatTimer) clearTimeout(heartbeatTimer);
    heartbeatEl.hidden = true;
    heartbeatTimer = setTimeout(() => {
      heartbeatEl.textContent = "⚠ Keine Events seit 30s — Pull könnte hängen oder fertig sein";
      heartbeatEl.hidden = false;
    }, 30_000);
  };

  const { io: ioClient } = await import("socket.io-client");
  const syncSocket = ioClient("/sync", { reconnection: false });
  syncSocket.on("connect", () => syncSocket.emit("subscribe", slug));
  syncSocket.on("event", (e: { stage: string; message: string; detail?: unknown }) => {
    resetHeartbeat();
    // In-place updating live-counter for the high-frequency progress stages.
    if (e.stage === "rsync-progress" || e.stage === "db-progress") {
      liveLine.textContent = e.message;
      return;
    }
    // All other stages append to the log normally.
    append(`[${e.stage}] ${e.message}`, e.stage === "error" ? "err-line" : "stage-line");
    if (e.stage === "done") {
      liveLine.textContent = "";
      const d = e.detail as { durationMs?: number; healthStatus?: number } | undefined;
      const secs = d?.durationMs !== undefined ? (d.durationMs / 1000).toFixed(1) : "?";
      append(`--- Fertig in ${secs}s, health=${d?.healthStatus ?? "?"}`, "stage-line");
      if (heartbeatTimer) { clearTimeout(heartbeatTimer); heartbeatEl.hidden = true; }
      isDone = true;
      submit.disabled = false;
      submit.textContent = "✓ Schließen";
      const frame = document.getElementById("staging-frame") as HTMLIFrameElement;
      const opt = (document.getElementById("project-select") as HTMLSelectElement).selectedOptions[0];
      if (opt?.dataset.url) frame.src = opt.dataset.url;
      // Der Server hat die Drift-Markierung gerade gestrichen — sofort neu
      // laden, statt bis zum naechsten 15-Minuten-Poll rot zu bleiben.
      window.dispatchEvent(new CustomEvent("stage-app:pull-done", { detail: { slug } }));
    } else if (e.stage === "error") {
      liveLine.textContent = "";
      if (heartbeatTimer) { clearTimeout(heartbeatTimer); heartbeatEl.hidden = true; }
      submit.disabled = false;
      submit.textContent = "Erneut versuchen";
    }
  });
  m.addEventListener("DOMNodeRemoved", () => {
    syncSocket.close();
    if (heartbeatTimer) clearTimeout(heartbeatTimer);
  }, { once: true });

  (m.querySelector('[data-action="test-ssh"]') as HTMLButtonElement).addEventListener("click", async () => {
    setMsg(testMsg, "SSH-Test läuft …", "");
    const r = await api<{ ok?: boolean; output?: string; error?: string; stderr?: string }>(`/api/projects/${slug}/test-ssh`, "POST", {});
    if (r.status === 200 && r.data?.ok) {
      setMsg(testMsg, "SSH OK: " + (r.data.output ?? ""), "success");
    } else {
      setMsg(testMsg, "SSH-Fehler: " + (r.data?.error ?? r.data?.stderr ?? String(r.status)), "error");
    }
  });

  (m.querySelector('[data-action="test-live-db"]') as HTMLButtonElement).addEventListener("click", async () => {
    setMsg(testMsg, "Live-DB-Test läuft …", "");
    const r = await api<{ ok?: boolean; output?: string; error?: string; stderr?: string }>(`/api/projects/${slug}/test-live-db`, "POST", {});
    if (r.status === 200 && r.data?.ok) {
      setMsg(testMsg, "Live-DB OK", "success");
    } else {
      setMsg(testMsg, "Live-DB-Fehler: " + (r.data?.error ?? r.data?.stderr ?? String(r.status)), "error");
    }
  });

  submit.addEventListener("click", async () => {
    if (isDone) { m.remove(); return; }
    if (!confirm("Staging wird komplett überschrieben. Weiter?")) {
      // Ohne diese Zeile sieht ein weggeklicktes confirm() genauso aus wie ein
      // Pull, der gar nicht erst losgelaufen ist — man wartet auf Ausgabe, die
      // nie kommt.
      append("--- Abgebrochen, es wurde nichts verändert.", "stage-line");
      return;
    }
    submit.disabled = true; submit.textContent = "läuft …";
    append("--- Pull gestartet ---");
    const r = await api<{ started?: boolean; error?: string }>(
      `/api/projects/${slug}/pull`, "POST", { fast: fastCheckbox.checked },
    );
    if (r.status === 423) {
      const g = (r.data as { grund?: string } | null)?.grund ?? "Staging in Arbeit";
      append(`--- Pull gesperrt: ${g} — Sperre erst über das Schloss in der Toolbar lösen.`, "err-line");
      submit.disabled = true;
      submit.textContent = "Gesperrt";
      return;
    }
    if (r.status !== 202) {
      append("--- Fehler beim Start: " + (r.data?.error ?? String(r.status)), "err-line");
      submit.disabled = false;
      submit.textContent = "Erneut versuchen";
    }
    // Completion is handled by the /sync socket "done"/"error" events above.
  });
}

/**
 * Sammel-Pull: prueft frisch, welche Portale hinter Live liegen, holt eine
 * Bestaetigung ein und zieht dann genau die bestaetigten Portale nacheinander
 * (Server-Ablauf: src/routes/pull-all.ts, Design: docs/superpowers/specs/
 * 2026-08-15-sammel-pull-design.md).
 *
 * Drei Besonderheiten gegenueber openPullModal():
 *  - Die Pruefung (~3,5 Min) wird NICHT nur ueber den Socket abgewartet. Ein
 *    Neustart von stage-app waehrend der Pruefung liesse die Fertig-Meldung
 *    nie ankommen (die Listener des Kindprozesses haengen an der toten
 *    Request-Closure) — deshalb zusaetzlich ein Poll auf GET /api/stage-drift,
 *    der am Zeitstempel `geprueft` erkennt, dass etwas Frisches geschrieben
 *    wurde, plus eine Obergrenze als letzte Reissleine.
 *  - Eigene, dedizierte Socket-Verbindung fuer die gesamte Dialog-Laufzeit
 *    (wie bei jedem anderen Modal hier auch) — /sync erlaubt pro Verbindung
 *    nur ein "subscribe". Ein Portal-Pull-Dialog, den der Betreiber parallel
 *    offen hat, hat seine eigene Verbindung und bleibt unberuehrt.
 *  - Schliessen wird per MutationObserver erkannt, nicht per "DOMNodeRemoved"
 *    (Mutation Event, von Chrome/Edge seit Version 127 entfernt — siehe
 *    Kommentar unten). Ohne das haengen alle drei Wartephasen in Chrome an
 *    einem Listener, der dort nie feuert.
 */
async function openPullAllModal(): Promise<void> {
  const m = openModal("tpl-pull-all-modal");
  const modalRoot = document.getElementById("modal-root") as HTMLElement;
  const log = m.querySelector("[data-log]") as HTMLElement;
  const confirmBtn = m.querySelector("[data-confirm]") as HTMLButtonElement;
  const phasePruefung = m.querySelector('[data-phase="pruefung"]') as HTMLElement;
  const phaseBestaetigen = m.querySelector('[data-phase="bestaetigen"]') as HTMLElement;
  const phaseLaeuft = m.querySelector('[data-phase="laeuft"]') as HTMLElement;
  const phaseBilanz = m.querySelector('[data-phase="bilanz"]') as HTMLElement;
  const pruefStatus = phasePruefung.querySelector("[data-status]") as HTMLElement;
  const confirmMsg = phaseBestaetigen.querySelector("[data-confirm-msg]") as HTMLElement;
  const confirmList = phaseBestaetigen.querySelector("[data-confirm-list]") as HTMLElement;
  const runStatus = phaseLaeuft.querySelector("[data-run-status]") as HTMLElement;
  const runList = phaseLaeuft.querySelector("[data-run-list]") as HTMLElement;
  const bilanzEl = phaseBilanz.querySelector("[data-bilanz]") as HTMLElement;

  const append = (text: string, cls?: string): void => {
    const span = document.createElement("span");
    if (cls) span.className = cls;
    span.textContent = text + "\n";
    log.appendChild(span);
    log.scrollTop = log.scrollHeight;
  };

  const zeigeFehlerphase = (text: string): void => {
    phasePruefung.hidden = true;
    phaseBestaetigen.hidden = true;
    phaseLaeuft.hidden = true;
    phaseBilanz.hidden = false;
    bilanzEl.innerHTML = `<p class="pa-warnung">${escapeHtml(text)}</p>`;
  };

  let closed = false;
  const cleanupFns: Array<() => void> = [];
  const onClosed = (): void => {
    if (closed) return;
    closed = true;
    cleanupFns.forEach((fn) => fn());
  };

  // "DOMNodeRemoved" ist ein Mutation Event — Chrome und Edge haben diese
  // Familie seit Version 127 (Juli 2024) vollstaendig entfernt (Firefox und
  // Safari unterstuetzen sie noch, das Verhalten waere also browserabhaengig
  // gewesen). Ein Listener darauf feuert in Chrome schlicht nie, und alle drei
  // Wartephasen unten haengen an genau diesem einen Aufraeum-Trigger — inklusive
  // Socket und Heartbeat-Timer. MutationObserver ist der ueberall unterstuetzte
  // Ersatz (an anderer Stelle in dieser Datei schon fuer body-Attribute im
  // Einsatz) und beobachtet hier modal-root: verschwindet `m` aus dessen
  // Kindliste — sei es per Klick auf "Schliessen", per Klick auf den Backdrop
  // oder durch den "details[open]"-Zwischenschritt in openModal() — gilt der
  // Dialog als geschlossen.
  const closeObserver = new MutationObserver(() => {
    if (!modalRoot.contains(m)) {
      closeObserver.disconnect();
      onClosed();
    }
  });
  closeObserver.observe(modalRoot, { childList: true });
  cleanupFns.push(() => closeObserver.disconnect());

  try {
    const { io: ioClient } = await import("socket.io-client");
    const syncSocket = ioClient("/sync", { reconnection: false });
    syncSocket.on("connect", () => syncSocket.emit("subscribe", PULL_ALL_KANAL));
    cleanupFns.push(() => syncSocket.close());

    // Ein einziger "event"-Listener fuer die gesamte Dialog-Laufzeit; welche
    // Phase gerade zustaendig ist, entscheidet dieser austauschbare Handler.
    type SockEvent = { stage: string; message: string; detail?: unknown };
    let handler: ((e: SockEvent) => void) | null = null;
    syncSocket.on("event", (e: SockEvent) => handler?.(e));

    // --- Phase 1: frischer Drift-Check ------------------------------------
    append("--- Prüfung gestartet ---");
    const startResp = await api<{ started?: boolean; error?: string }>("/api/pull-all/pruefen", "POST", {});
    if (closed) return;
    if (startResp.status === 409) {
      pruefStatus.textContent = "Ein Sammellauf läuft bereits — bitte warten, bis er fertig ist, und dann erneut versuchen.";
      append("--- Konnte nicht starten: Sammellauf bereits aktiv ---", "err-line");
      return;
    }
    if (startResp.status !== 202) {
      pruefStatus.textContent = "Prüfung konnte nicht gestartet werden.";
      append("--- Fehler beim Start: " + (startResp.data?.error ?? String(startResp.status)), "err-line");
      return;
    }

    // Baseline VOR dem Warten holen, damit das Nachfragen einen festen
    // Vergleichswert hat (siehe Funktionskommentar oben).
    const baseline = await api<{ geprueft: string | null }>("/api/stage-drift");
    const baselineGeprueft = baseline.data?.geprueft ?? null;

    const pruefResult = await new Promise<{ ok: boolean; hinweis: string }>((resolve) => {
      let erledigt = false;
      const fertig = (ok: boolean, hinweis: string): void => {
        if (erledigt) return;
        erledigt = true;
        clearInterval(pollTimer);
        clearTimeout(deadlineTimer);
        handler = null;
        resolve({ ok, hinweis });
      };

      handler = (e) => {
        const d = e.detail as { art?: string } | undefined;
        if (d?.art === "pruefung") fertig(e.stage !== "error", e.message);
      };

      // Fallback: ueberlebt einen Server-Neustart waehrend der Pruefung, siehe
      // Funktionskommentar. Alle 10s nachfragen ist billig genug fuer eine
      // GET-Route, die nur eine kleine JSON-Datei liest. `.catch()` am Ende,
      // weil sonst jeder Poll bei einer wackligen Leitung eine unbehandelte
      // Ablehnung waere — bis zu 66 Mal ueber die volle Wartezeit.
      //
      // Bewusst `g !== baselineGeprueft`, NICHT `g && g !== baselineGeprueft`:
      // claude_stage_drift.mjs schreibt data/stage_drift_alert.json nur, wenn
      // etwas ansteht, und LOESCHT sie, wenn alles sauber ist — dann liefert
      // GET /api/stage-drift `geprueft: null`. Mit `g &&` haette ausgerechnet
      // der gutmuetigste Ausgang (frische Pruefung findet: alles aktuell) das
      // Nachfragen nie ausgeloest; bliebe zusaetzlich die Socket-Meldung aus
      // (der Neustart-Fall, fuer den dieses Nachfragen ueberhaupt existiert),
      // waere der Dialog erst nach der 11-Minuten-Grenze fertig geworden — mit
      // einer falschen "hat zu lange gedauert"-Meldung fuer einen Lauf, der
      // sauber durchlief.
      //
      // Bekannte, bewusst nicht geloeste Einschraenkung: Das Nachfragen ist
      // nicht an DIESEN Lauf gebunden. Schreibt der naechtliche Cron (04:40
      // Uhr) waehrend des Wartens dieselbe Datei neu, wertet dieser Poll das
      // als "unsere Pruefung ist fertig" und Phase 2 arbeitet mit der (dann
      // zufaellig fremden) Cron-Liste weiter. Eine echte Bindung braeuchte
      // eine Lauf-Kennung im Server-Response (src/routes/pull-all.ts) — das
      // ist Server-Code, den dieser Umbau nicht anfasst.
      const pollTimer = setInterval(() => {
        if (erledigt) return;
        void api<{ geprueft: string | null }>("/api/stage-drift")
          .then((r) => {
            const g = r.data?.geprueft ?? null;
            if (g !== baselineGeprueft) fertig(true, "Prüfung fertig (per Nachfragen erkannt)");
          })
          .catch(() => { /* naechster Poll in 10s versucht es erneut */ });
      }, 10_000);

      // Obergrenze etwas ueber dem serverseitigen Zeitlimit (PRUEF_ZEITLIMIT_MS
      // = 10 Min, src/routes/pull-all.ts) — sonst wartet der Dialog laenger als
      // der Server selbst je brauchen wuerde, ohne je etwas zu melden.
      const deadlineTimer = setTimeout(() => {
        fertig(false, "Prüfung hat zu lange gedauert (> 11 Minuten) — abgebrochen. Bitte später erneut versuchen.");
      }, 11 * 60_000);

      // Schliesst der Betreiber den Dialog waehrend des Wartens, muss dieses
      // Promise trotzdem settlen — sonst haengt die Funktion an einem nie
      // aufgeloesten Promise und haelt eine losgeloeste DOM-Referenz (m/log)
      // am Leben, obwohl niemand mehr zusieht.
      cleanupFns.push(() => fertig(false, "Dialog geschlossen"));
    });

    if (closed) return;
    append(`--- ${pruefResult.hinweis} ---`, pruefResult.ok ? "stage-line" : "err-line");
    window.dispatchEvent(new CustomEvent("stage-app:pull-done", { detail: {} }));

    if (!pruefResult.ok) {
      pruefStatus.textContent = pruefResult.hinweis;
      return;
    }

    // --- Phase 2: Liste laden + Bestaetigung einholen ----------------------
    await ladePullSperren();
    const drift = await api<{ geprueft: string | null; portale: DriftPortal[] }>("/api/stage-drift");
    const faellige = (drift.data?.portale ?? []).filter((d) => d.pullNoetig);

    phasePruefung.hidden = true;
    phaseBestaetigen.hidden = false;

    if (faellige.length === 0) {
      confirmMsg.textContent = "Alle Portale sind aktuell — kein Pull nötig.";
      append("--- Nichts zu tun: keine Portale mit Drift ---", "stage-line");
      return;
    }

    confirmMsg.innerHTML =
      `<strong>${faellige.length} Portal${faellige.length === 1 ? "" : "e"}</strong> ` +
      `liegen hinter Live zurück. Beim Ziehen wird der jeweilige Staging-Stand komplett überschrieben.`;
    // Auswahl per Haken (Uli 30.09.2026): Portale, an deren Staging gerade gebaut wird (z. B. faszinierend
    // beim RÜCKEN-Lounge-Relaunch), lassen sich abwählen — der Pull würde deren Staging-Stand überschreiben.
    // Abgewählte Portale merkt sich der Browser (localStorage, nur Komfort) und lässt sie beim nächsten Mal
    // wieder aus; der Server zieht ohnehin nur die übergebenen Slugs (siehe src/routes/pull-all.ts).
    const ABGEWAEHLT_KEY = "stage-app:pull-all:abgewaehlt";
    const abgewaehlt = new Set<string>((() => {
      try { const v = JSON.parse(localStorage.getItem(ABGEWAEHLT_KEY) ?? "[]"); return Array.isArray(v) ? v.map(String) : []; }
      catch { return []; }
    })());
    confirmList.innerHTML = faellige.map((d) => {
      const detail = escapeHtml(driftText(d) || "Drift erkannt");
      const slug = escapeHtml(d.slug);
      const sperre = pullSperren[d.slug];
      if (sperre) {
        return `<li data-slug="${slug}" class="pa-abgewaehlt pa-gesperrt"><label class="pa-wahl"><input type="checkbox" data-pa-haken value="${slug}" disabled>` +
          `<span class="pa-slug">🔒 ${slug}</span><span class="pa-detail">gesperrt: ${escapeHtml(sperre.grund)}</span></label></li>`;
      }
      const an = abgewaehlt.has(d.slug) ? "" : " checked";
      return `<li data-slug="${slug}"><label class="pa-wahl"><input type="checkbox" data-pa-haken value="${slug}"${an}>` +
        `<span class="pa-slug">${slug}</span><span class="pa-detail">${detail}</span></label></li>`;
    }).join("");
    const haken = Array.from(confirmList.querySelectorAll<HTMLInputElement>("input[data-pa-haken]:not(:disabled)"));
    const gewaehlt = (): string[] => haken.filter((h) => h.checked).map((h) => h.value);
    const aktualisiereAuswahl = (): void => {
      const n = gewaehlt().length;
      confirmBtn.disabled = n === 0;
      confirmBtn.textContent = n === 0 ? "Kein Portal ausgewählt" : `Jetzt ${n} Portal${n === 1 ? "" : "e"} ziehen`;
      for (const h of haken) h.closest("li")?.classList.toggle("pa-abgewaehlt", !h.checked);
    };
    for (const h of haken) {
      h.addEventListener("change", () => {
        if (h.checked) abgewaehlt.delete(h.value); else abgewaehlt.add(h.value);
        try { localStorage.setItem(ABGEWAEHLT_KEY, JSON.stringify(Array.from(abgewaehlt))); } catch { /* nur Komfort */ }
        aktualisiereAuswahl();
      });
    }

    confirmBtn.hidden = false;
    aktualisiereAuswahl();
    let slugs: string[] = [];

    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        confirmBtn.removeEventListener("click", onConfirm);
        resolve();
      };
      const onConfirm = (): void => { slugs = gewaehlt(); if (slugs.length) finish(); };
      confirmBtn.addEventListener("click", onConfirm);
      // Schliesst der Betreiber statt zu bestaetigen, muss dieses Promise
      // trotzdem settlen (siehe Kommentar bei der Pruefungs-Wartephase oben).
      cleanupFns.push(finish);
    });
    if (closed) return;

    confirmBtn.disabled = true;
    confirmBtn.hidden = true;

    // --- Phase 3: Sammel-Pull starten + Fortschritt -------------------------
    const startPull = await api<{ started?: boolean; anzahl?: number; error?: string }>(
      "/api/pull-all", "POST", { slugs },
    );
    if (closed) return;
    if (startPull.status !== 202) {
      const grund = startPull.data?.error === "sammellauf_aktiv"
        ? "Ein Sammellauf läuft bereits."
        : startPull.data?.error === "pull_gesperrt"
          ? "Mindestens ein Portal ist gesperrt (Schloss) — bitte Dialog neu öffnen."
        : startPull.data?.error === "unbekannte_portale"
          ? "Mindestens ein Portal ist unbekannt — bitte Dialog neu öffnen."
          : startPull.data?.error === "keine_portale"
            ? "Keine Portale übergeben — bitte Dialog neu öffnen."
            : `Fehler beim Start (${startPull.status}).`;
      zeigeFehlerphase(`Konnte nicht starten: ${grund}`);
      append("--- Fehler beim Start des Sammel-Pulls: " + grund, "err-line");
      return;
    }

    phaseBestaetigen.hidden = true;
    phaseLaeuft.hidden = false;
    runStatus.textContent = `Läuft: 0 / ${slugs.length}`;
    runList.innerHTML = slugs.map((s) =>
      `<li data-slug="${escapeHtml(s)}"><span class="pa-slug">${escapeHtml(s)}</span><span class="pa-status">wartet</span></li>`,
    ).join("");
    append(`--- Sammel-Pull gestartet für ${slugs.length} Portale ---`);

    const statusLabel: Record<string, string> = {
      laeuft: "läuft …",
      erledigt: "✓ erledigt",
      gepullt_aber_defekt: "⛔ gepullt, aber defekt",
      uebersprungen: "übersprungen",
      fehlgeschlagen: "fehlgeschlagen",
      zeitueberschreitung: "Zeitüberschreitung",
    };

    // Kein Fortschritt fuer eine Weile heisst hier nicht zwingend "haengt" —
    // ein einzelnes Portal darf bis zu 15 Minuten dauern (ZEITLIMIT_MS,
    // src/routes/pull-all.ts). Die Warnung ist ein Hinweis, kein Abbruch, und
    // es gibt hier bewusst KEIN Nachfragen-Fallback wie in Phase 1: anders als
    // dort laeuft die eigentliche Arbeit (rsync/mysqldump je Portal) im
    // Hintergrund weiter und die Liste oben (Drift-Fahne) zeigt nach jedem
    // erfolgreichen Einzel-Pull ohnehin frisch, sobald neu geladen wird. Die
    // Warnung unten deckt damit bislang nur den Neustart-Fall verstaendlich ab
    // (Socket tot, aber der Hintergrundlauf existiert noch) — einen einfachen
    // Verbindungsabriss ohne Neustart unterscheidet der Text nicht extra.
    let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
    const heartbeatEl = document.createElement("div");
    heartbeatEl.className = "pull-all-status";
    heartbeatEl.style.color = "#f59e0b";
    heartbeatEl.hidden = true;
    phaseLaeuft.appendChild(heartbeatEl);
    const resetHeartbeat = (): void => {
      if (heartbeatTimer) clearTimeout(heartbeatTimer);
      heartbeatEl.hidden = true;
      heartbeatTimer = setTimeout(() => {
        heartbeatEl.textContent =
          "⚠ Keine Fortschritts-Events seit 90s — ein einzelnes Portal darf bis zu 15 Minuten dauern. " +
          "Möglich ist auch ein Server-Neustart; in dem Fall bitte die Seite neu laden und den Drift-Stand prüfen.";
        heartbeatEl.hidden = false;
      }, 90_000);
    };
    resetHeartbeat();
    cleanupFns.push(() => { if (heartbeatTimer) clearTimeout(heartbeatTimer); });

    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        handler = null;
        resolve();
      };
      // Schliesst der Betreiber den Dialog, bevor die Bilanz da ist, muss auch
      // dieses Promise settlen (siehe Kommentar bei der Pruefungs-Wartephase
      // oben) — der Sammel-Pull selbst laeuft serverseitig unbeeinflusst weiter.
      cleanupFns.push(finish);

      handler = (e) => {
        const d = e.detail as
          | { art?: string; slug?: string; status?: string; index?: number; gesamt?: number;
              erledigt?: number; defekt?: number; fehlgeschlagen?: number; uebersprungen?: number }
          | undefined;
        if (!d || d.art === "pruefung") return;

        resetHeartbeat();

        if (d.art === "bilanz" || d.art === "bilanz_fehler") {
          if (heartbeatTimer) clearTimeout(heartbeatTimer);
          heartbeatEl.hidden = true;
          phaseLaeuft.hidden = true;
          phaseBilanz.hidden = false;
          if (d.art === "bilanz_fehler") {
            bilanzEl.innerHTML = '<p class="pa-warnung">Sammel-Pull unerwartet abgebrochen — Details im Log unten.</p>';
          } else {
            const defekt = d.defekt ?? 0;
            const defektZeile = defekt > 0
              ? `<p class="pa-warnung">⛔ ${defekt} Portal${defekt === 1 ? "" : "e"} gepullt, aber die Startseite ist defekt — bitte ansehen.</p>`
              : "";
            bilanzEl.innerHTML =
              `<p>${d.erledigt ?? 0} erledigt, ${defekt} defekt, ${d.fehlgeschlagen ?? 0} fehlgeschlagen, ${d.uebersprungen ?? 0} übersprungen.</p>` +
              defektZeile;
          }
          append(`--- ${e.message} ---`, e.stage === "error" ? "err-line" : "stage-line");
          window.dispatchEvent(new CustomEvent("stage-app:pull-done", { detail: {} }));
          finish();
          return;
        }

        if (d.slug && d.status && typeof d.index === "number" && typeof d.gesamt === "number") {
          runStatus.textContent = `Portal ${d.index + 1} / ${d.gesamt}: ${d.slug}`;
          const li = Array.from(runList.children).find(
            (el) => (el as HTMLElement).dataset.slug === d.slug,
          ) as HTMLElement | undefined;
          if (li) {
            li.className = `pa-${d.status}`;
            const statusSpan = li.querySelector(".pa-status");
            if (statusSpan) statusSpan.textContent = statusLabel[d.status] ?? d.status;
          }
          const logCls = d.status === "erledigt" || d.status === "laeuft" ? "stage-line" : "err-line";
          append(`[${d.index + 1}/${d.gesamt}] ${d.slug}: ${statusLabel[d.status] ?? d.status}`, logCls);
        }
      };
    });
  } catch (err) {
    if (closed) return;
    // Netzwerkfehler (z.B. ein kurzer WLAN-Aussetzer) sind hier der einzig
    // realistische Fehlerweg — die Server-Antworten selbst werden oben ueber
    // ihren Status ausgewertet, nicht ueber eine Ablehnung. Ohne dieses Netz
    // bliebe der Dialog beim zuletzt gezeigten Text stehen (z.B. dauerhaft
    // "Portale werden geprüft …"), im schlimmsten Fall mit bereits
    // verstecktem Bestaetigen-Knopf — ohne jede Fehlermeldung oder
    // Handlungsoption ausser dem globalen "Schliessen".
    const msg = err instanceof Error ? err.message : String(err);
    zeigeFehlerphase(`Unerwarteter Fehler: ${msg} — bitte Verbindung prüfen und den Dialog neu öffnen.`);
    append("--- Unerwarteter Fehler: " + msg + " ---", "err-line");
  }
}

async function openPublishModal(): Promise<void> {
  const slug = currentSlug();
  if (!slug) return;
  const m = openModal("tpl-publish-modal");
  const log = m.querySelector("[data-log]") as HTMLElement;
  const diff = m.querySelector("[data-diff]") as HTMLElement;
  const dryRunMsg = m.querySelector('[data-msg="dryrun"]') as HTMLElement;
  const confirmInput = m.querySelector('input[name="confirmSlug"]') as HTMLInputElement;
  const submit = m.querySelector("[data-submit]") as HTMLButtonElement;
  const refreshBtn = m.querySelector('[data-action="refresh-dryrun"]') as HTMLButtonElement;
  confirmInput.placeholder = slug;
  let isDone = false;

  const append = (text: string, cls?: string) => {
    const span = document.createElement("span");
    if (cls) span.className = cls;
    span.textContent = text + "\n";
    log.appendChild(span);
    log.scrollTop = log.scrollHeight;
  };

  const { io: ioClient } = await import("socket.io-client");
  const syncSocket = ioClient("/sync", { reconnection: false });
  syncSocket.on("connect", () => syncSocket.emit("subscribe", slug));
  syncSocket.on("event", (e: { stage: string; message: string; detail?: unknown }) => {
    append(`[${e.stage}] ${e.message}`, e.stage === "error" ? "err-line" : "stage-line");
    if (e.stage === "done") {
      const d = e.detail as { durationMs?: number; filesChanged?: number; filesAdded?: number; filesDeleted?: number; backupDir?: string } | undefined;
      const secs = d?.durationMs !== undefined ? (d.durationMs / 1000).toFixed(1) : "?";
      append(
        `--- Fertig in ${secs}s — geändert: ${d?.filesChanged ?? 0}, neu: ${d?.filesAdded ?? 0}, gelöscht: ${d?.filesDeleted ?? 0}\n` +
        `--- Backup auf Live: ${d?.backupDir ?? "(unbekannt)"}`,
        "stage-line",
      );
      isDone = true;
      submit.disabled = false;
      submit.textContent = "✓ Schließen";
    } else if (e.stage === "error") {
      submit.disabled = confirmInput.value !== slug;
      submit.textContent = "Erneut versuchen";
    }
  });
  m.addEventListener("DOMNodeRemoved", () => syncSocket.close(), { once: true });

  const renderDiff = (d: { changed: string[]; added: string[]; deleted: string[] }) => {
    const total = d.changed.length + d.added.length + d.deleted.length;
    if (total === 0) {
      diff.innerHTML = '<p class="diff-empty">Keine Änderungen — Live ist bereits identisch zu Staging.</p>';
      return;
    }
    const section = (label: string, items: string[]) => {
      if (items.length === 0) return "";
      const lis = items.map((p) => `<li>${escapeHtml(p)}</li>`).join("");
      return `<details class="diff-section"><summary>${label} (${items.length})</summary><ul>${lis}</ul></details>`;
    };
    diff.innerHTML = section("Geändert", d.changed) + section("Neu", d.added) + section("Gelöscht", d.deleted);
  };

  const fetchDryRun = async () => {
    setMsg(dryRunMsg, "Vorschau wird berechnet …", "");
    diff.innerHTML = '<p class="diff-empty">Vorschau wird geladen …</p>';
    const r = await api<{ ok?: boolean; changed?: string[]; added?: string[]; deleted?: string[]; error?: string }>(
      `/api/projects/${slug}/publish/dry-run`, "POST", {},
    );
    if (r.status === 200 && r.data?.ok) {
      setMsg(dryRunMsg, "", "");
      renderDiff({ changed: r.data.changed ?? [], added: r.data.added ?? [], deleted: r.data.deleted ?? [] });
    } else {
      setMsg(dryRunMsg, "Fehler bei Vorschau: " + (r.data?.error ?? String(r.status)), "error");
      diff.innerHTML = "";
    }
  };

  refreshBtn.addEventListener("click", () => { void fetchDryRun(); });
  confirmInput.addEventListener("input", () => {
    submit.disabled = confirmInput.value !== slug;
  });

  submit.addEventListener("click", async () => {
    if (isDone) { m.remove(); return; }
    if (confirmInput.value !== slug) return;
    submit.disabled = true; submit.textContent = "läuft …";
    append("--- Publish gestartet ---");
    const r = await api<{ started?: boolean; error?: string }>(
      `/api/projects/${slug}/publish`, "POST", {},
    );
    if (r.status !== 202) {
      append("--- Fehler beim Start: " + (r.data?.error ?? String(r.status)), "err-line");
      submit.disabled = false;
      submit.textContent = "Erneut versuchen";
    }
    // Completion handled by the /sync socket "done"/"error" events above.
  });

  void fetchDryRun();
}

async function openContentPublishModal(): Promise<void> {
  const slug = currentSlug();
  if (!slug) return;
  const m = openModal("tpl-content-publish-modal");
  const log = m.querySelector("[data-log]") as HTMLElement;
  const diffArea = m.querySelector("[data-diff]") as HTMLElement;
  const diffMsg = m.querySelector('[data-msg="diff"]') as HTMLElement;
  const confirmInput = m.querySelector('input[name="confirmSlug"]') as HTMLInputElement;
  const submit = m.querySelector("[data-submit]") as HTMLButtonElement;
  const refreshBtn = m.querySelector('[data-action="refresh-diff"]') as HTMLButtonElement;
  confirmInput.placeholder = slug;

  let lastDiff: {
    changed: Array<{ id: number; title: string; stagingModified: string | null; liveModified: string | null; differingColumns: Array<{ name: string; live: string | null; staging: string | null }> }>;
    conflicts: Array<{ id: number; title: string; stagingModified: string | null; liveModified: string | null; differingColumns: Array<{ name: string; live: string | null; staging: string | null }> }>;
    stagingOnly: Array<{ id: number; title: string }>;
    liveOnly: Array<{ id: number; title: string }>;
  } = { changed: [], conflicts: [], stagingOnly: [], liveOnly: [] };
  let isDone = false;

  const append = (text: string, cls?: string) => {
    const span = document.createElement("span");
    if (cls) span.className = cls;
    span.textContent = text + "\n";
    log.appendChild(span);
    log.scrollTop = log.scrollHeight;
  };

  const updateSubmitState = () => {
    if (isDone) return;
    const slugOk = confirmInput.value === slug;
    const anyChecked = !!diffArea.querySelector('input[type="checkbox"][data-row-id]:checked');
    submit.disabled = !(slugOk && anyChecked);
  };

  const renderDiff = () => {
    const total = lastDiff.changed.length + lastDiff.conflicts.length;
    if (total === 0 && lastDiff.stagingOnly.length === 0 && lastDiff.liveOnly.length === 0) {
      diffArea.innerHTML = '<p class="diff-empty">Keine Änderungen — Staging und Live sind identisch (für Whitelist-Spalten).</p>';
      updateSubmitState();
      return;
    }
    const fmtRow = (r: typeof lastDiff.changed[number], cls: string, defaultChecked: boolean): string => {
      const cols = r.differingColumns.map((c) => escapeHtml(c.name)).join(", ");
      const checkbox = `<input type="checkbox" data-row-id="${r.id}" ${defaultChecked ? "checked" : ""}>`;
      const fieldDiff = r.differingColumns.map((c) => {
        const { liveHtml, stagingHtml } = renderInlineDiff(c.live, c.staging);
        return `<tr><td class="cell-name">${escapeHtml(c.name)}</td><td class="cell-live"><pre>${liveHtml}</pre></td><td class="cell-staging"><pre>${stagingHtml}</pre></td></tr>`;
      }).join("");
      return `
        <li class="diff-row ${cls}" data-row-id="${r.id}">
          <label>${checkbox} #${r.id} <strong>${escapeHtml(r.title)}</strong>
            <span class="diff-meta">stage ${escapeHtml(r.stagingModified ?? "?")} / live ${escapeHtml(r.liveModified ?? "?")}</span>
            <span class="diff-cols">› Felder: ${cols}</span>
          </label>
          <details><summary>Expand für Diff</summary>
            <table class="field-diff"><thead><tr><th>Spalte</th><th class="th-live">Live (alt)</th><th class="th-staging">Staging (neu)</th></tr></thead><tbody>${fieldDiff}</tbody></table>
          </details>
        </li>`;
    };
    diffArea.innerHTML = `
      <details class="diff-section" open>
        <summary>Geändert (${lastDiff.changed.length})</summary>
        <ul>${lastDiff.changed.map((r) => fmtRow(r, "row-changed", true)).join("")}</ul>
      </details>
      <details class="diff-section ${lastDiff.conflicts.length > 0 ? "conflict-section" : ""}" ${lastDiff.conflicts.length > 0 ? "open" : ""}>
        <summary>Konflikt (${lastDiff.conflicts.length}) ${lastDiff.conflicts.length > 0 ? " — Live wurde nach Pull editiert ⚠" : ""}</summary>
        <ul>${lastDiff.conflicts.map((r) => fmtRow(r, "row-conflict", false)).join("")}</ul>
      </details>
      <details class="diff-section">
        <summary>Nur auf Staging (${lastDiff.stagingOnly.length}) — werden NICHT publiziert</summary>
        <ul>${lastDiff.stagingOnly.map((r) => `<li>#${r.id} ${escapeHtml(r.title)}</li>`).join("")}</ul>
      </details>
      <details class="diff-section">
        <summary>Nur auf Live (${lastDiff.liveOnly.length}) — werden nicht angefasst</summary>
        <ul>${lastDiff.liveOnly.map((r) => `<li>#${r.id} ${escapeHtml(r.title)}</li>`).join("")}</ul>
      </details>
    `;
    diffArea.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-row-id]').forEach((cb) => {
      cb.addEventListener("change", updateSubmitState);
    });
    updateSubmitState();
  };

  const fetchDiff = async () => {
    setMsg(diffMsg, "Diff wird geladen …", "");
    diffArea.innerHTML = '<p class="diff-empty">Diff wird geladen …</p>';
    const r = await api<{ ok?: boolean; changed?: typeof lastDiff.changed; conflicts?: typeof lastDiff.conflicts; stagingOnly?: typeof lastDiff.stagingOnly; liveOnly?: typeof lastDiff.liveOnly; error?: string; generatedAt?: string }>(
      `/api/projects/${slug}/content-publish/diff`, "POST", {},
    );
    if (r.status === 200 && r.data?.ok) {
      lastDiff = {
        changed: r.data.changed ?? [],
        conflicts: r.data.conflicts ?? [],
        stagingOnly: r.data.stagingOnly ?? [],
        liveOnly: r.data.liveOnly ?? [],
      };
      setMsg(diffMsg, `Geladen ${r.data.generatedAt ?? ""}`, "success");
      renderDiff();
    } else {
      setMsg(diffMsg, "Fehler bei Diff: " + (r.data?.error ?? String(r.status)), "error");
      diffArea.innerHTML = "";
    }
  };

  refreshBtn.addEventListener("click", () => { void fetchDiff(); });
  confirmInput.addEventListener("input", updateSubmitState);

  submit.addEventListener("click", async () => {
    if (isDone) { m.remove(); return; }
    if (confirmInput.value !== slug) return;
    const ids: number[] = [];
    const forceConflictIds: number[] = [];
    diffArea.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-row-id]:checked').forEach((cb) => {
      const id = Number(cb.dataset["rowId"]);
      ids.push(id);
      if (cb.closest(".row-conflict")) forceConflictIds.push(id);
    });
    if (ids.length === 0) return;
    if (forceConflictIds.length > 0) {
      if (!confirm(`Du überschreibst ${forceConflictIds.length} Live-Edit(s) mit Staging's Version. Live's Änderungen seit Pull gehen verloren (außer im Backup). Wirklich fortfahren?`)) return;
    }
    submit.disabled = true; submit.textContent = "läuft …";
    append("--- Content Publish gestartet ---");
    const r = await api<{ ok?: boolean; backupPath?: string; affected?: Array<{ id: number; ok: boolean; error?: string }>; error?: string }>(
      `/api/projects/${slug}/content-publish`, "POST", { ids, forceConflictIds },
    );
    if (r.status === 200 && r.data?.ok) {
      const ok = r.data.affected?.filter((a) => a.ok).length ?? 0;
      const failed = r.data.affected?.filter((a) => !a.ok) ?? [];
      append(`--- Fertig: ${ok} updated, ${failed.length} failed`, "stage-line");
      append(`--- Backup: ${r.data.backupPath ?? "(unbekannt)"}`, "stage-line");
      for (const f of failed) append(`  #${f.id}: ${f.error ?? "?"}`, "err-line");
      isDone = true;
      submit.disabled = false;
      submit.textContent = "✓ Schließen";
    } else {
      append("--- Fehler: " + (r.data?.error ?? String(r.status)), "err-line");
      submit.disabled = false;
      submit.textContent = "Erneut versuchen";
    }
  });

  void fetchDiff();
}

function tokenizeForDiff(s: string): string[] {
  return s.match(/[\p{L}\p{N}_]+|\s+|[^\p{L}\p{N}_\s]/gu) ?? [];
}

interface DiffOp { type: "eq" | "del" | "add"; token: string }

function diffTokens(a: string[], b: string[]): DiffOp[] {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) dp[i]![j] = dp[i - 1]![j - 1]! + 1;
      else dp[i]![j] = Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
    }
  }
  const ops: DiffOp[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) { ops.push({ type: "eq", token: a[i - 1]! }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i]![j - 1]! >= dp[i - 1]![j]!)) { ops.push({ type: "add", token: b[j - 1]! }); j--; }
    else { ops.push({ type: "del", token: a[i - 1]! }); i--; }
  }
  return ops.reverse();
}

function renderInlineDiff(live: string | null, staging: string | null): { liveHtml: string; stagingHtml: string } {
  const lv = live ?? "(NULL)";
  const sv = staging ?? "(NULL)";
  if (live === null || staging === null) {
    return { liveHtml: escapeHtml(lv), stagingHtml: escapeHtml(sv) };
  }
  const a = tokenizeForDiff(lv);
  const b = tokenizeForDiff(sv);
  // Cap LCS work; previews are already truncated to 500 chars upstream so this
  // rarely fires, but guard against pathological inputs.
  if (a.length * b.length > 200_000) {
    return { liveHtml: escapeHtml(lv), stagingHtml: escapeHtml(sv) };
  }
  const ops = diffTokens(a, b);
  let liveHtml = "";
  let stagingHtml = "";
  for (const op of ops) {
    if (op.type === "eq") {
      const t = escapeHtml(op.token);
      liveHtml += t;
      stagingHtml += t;
    } else if (op.type === "del") {
      liveHtml += `<span class="diff-del">${escapeHtml(op.token)}</span>`;
    } else {
      stagingHtml += `<span class="diff-ins">${escapeHtml(op.token)}</span>`;
    }
  }
  return { liveHtml, stagingHtml };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c] as string));
}

function openModal(templateId: string): HTMLElement {
  const tpl = document.getElementById(templateId) as HTMLTemplateElement;
  const root = document.getElementById("modal-root") as HTMLElement;
  const clone = tpl.content.cloneNode(true) as DocumentFragment;
  const backdrop = clone.firstElementChild as HTMLElement;
  root.appendChild(backdrop);
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) backdrop.remove(); });
  backdrop.querySelectorAll("[data-close]").forEach((btn) => {
    btn.addEventListener("click", () => {
      // Gestuftes Schliessen (03.08.2026, Uli): Ist im Modal ein Eintrag aufgeklappt
      // (<details open>, z.B. ein Blog-Artikel in der Blog-Automatik), schliesst der Button
      // ZUERST diesen Eintrag. Erst wenn nichts mehr offen ist, wird das Fenster geschlossen.
      // Vorher warf ein Klick den Nutzer direkt aus der ganzen Suite.
      const offen = backdrop.querySelectorAll<HTMLDetailsElement>("details[open]");
      if (offen.length) {
        offen.forEach((d) => { d.open = false; });
        return;
      }
      backdrop.remove();
    });
  });
  // Delegated password-reveal handler — toggles input type between "password"
  // and "text" on click of any .reveal-btn inside this modal. Works for the
  // wizard's password fields, the edit-project form's, and the change-password
  // form's, without per-modal wiring.
  backdrop.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains("reveal-btn")) return;
    e.preventDefault();
    const input = target.previousElementSibling as HTMLInputElement | null;
    if (!input) return;
    const showing = input.type === "text";
    input.type = showing ? "password" : "text";
    target.textContent = showing ? "👁" : "🙈";
  });
  return backdrop;
}

/** Dialog zum Setzen/Lösen der Pull-Sperre für `slug`; ruft nach jeder Änderung `nachher()` auf. */
function openPullSperreModal(slug: string, name: string, nachher: () => void): void {
  const m = openModal("tpl-pull-sperre-modal");
  const titel = m.querySelector("[data-titel]") as HTMLElement;
  const info = m.querySelector("[data-info]") as HTMLElement;
  const feld = m.querySelector("[data-feld]") as HTMLElement;
  const grund = m.querySelector('input[name="grund"]') as HTMLInputElement;
  const msg = m.querySelector("[data-msg]") as HTMLElement;
  const submit = m.querySelector("[data-submit]") as HTMLButtonElement;
  const s = pullSperren[slug];
  titel.textContent = s ? `🔒 Pull-Sperre: ${name}` : `🔓 Pull-Sperre setzen: ${name}`;
  if (s) {
    info.innerHTML = `Gesperrt seit <strong>${escapeHtml(formatDate(s.seit))}</strong> · Grund: <strong>${escapeHtml(s.grund)}</strong><br>` +
      "Solange die Sperre besteht, lehnt die Stage-App jeden Pull dieses Portals ab — auch über die Fahne „Pull nötig“ und im Sammel-Pull.";
    feld.hidden = true;
    submit.textContent = "Sperre lösen";
  } else {
    info.textContent = "Sperren, solange an diesem Staging gebaut wird: Dann kann es kein Pull (Einzel-Pull, Fahne „Pull nötig“, Sammel-Pull) mit Live überschreiben. Lösen jederzeit hier.";
    grund.value = "Staging in Arbeit";
    submit.textContent = "Sperren";
  }
  submit.addEventListener("click", async () => {
    submit.disabled = true;
    const r = await api<{ ok?: boolean; error?: string }>(`/api/projects/${encodeURIComponent(slug)}/pull-sperre`, "PUT",
      s ? { gesperrt: false } : { gesperrt: true, grund: grund.value });
    if (r.status === 200 && r.data?.ok) {
      await ladePullSperren();
      nachher();
      m.remove();
    } else {
      submit.disabled = false;
      setMsg(msg, "Fehler: " + (r.data?.error ?? String(r.status)), "error");
    }
  });
}

function currentSlug(): string | null {
  const sel = document.getElementById("project-select") as HTMLSelectElement;
  return sel.selectedOptions[0]?.value ?? null;
}

function td(text: string, className?: string): HTMLTableCellElement {
  const el = document.createElement("td");
  el.textContent = text;
  if (className) el.className = className;
  return el;
}

async function doSnapshot(label: string, keepForever: boolean): Promise<void> {
  const slug = currentSlug();
  if (!slug) return;
  const res = await api<{ snapshot: Snapshot; error?: string }>(
    `/api/projects/${slug}/snapshots`, "POST",
    { label: label || undefined, keepForever },
  );
  if (res.status !== 201) alert("Snapshot fehlgeschlagen: " + (res.data?.error ?? String(res.status)));
}

async function doUndo(): Promise<void> {
  const slug = currentSlug();
  if (!slug) return;
  const res = await api<{ error?: string }>(`/api/projects/${slug}/undo`, "POST", {});
  if (res.status !== 200) alert("Undo fehlgeschlagen: " + (res.data?.error ?? String(res.status)));
}

async function renderHistory(body: HTMLElement): Promise<void> {
  const slug = currentSlug();
  if (!slug) return;
  const res = await api<{ snapshots: Snapshot[] }>(`/api/projects/${slug}/snapshots`);
  body.innerHTML = "";
  for (const s of res.data?.snapshots ?? []) {
    const tr = document.createElement("tr");
    tr.appendChild(td(formatDate(s.created_at)));
    tr.appendChild(td(s.type, "row-type"));
    tr.appendChild(td(s.label ?? ""));
    tr.appendChild(td(formatBytes(s.size_bytes)));
    tr.appendChild(td(s.keep_forever ? "✓" : ""));
    const actions = document.createElement("td");
    tr.appendChild(actions);
    const btnRestore = document.createElement("button");
    btnRestore.textContent = "Restore";
    btnRestore.addEventListener("click", async () => {
      if (!confirm("Diesen Snapshot wiederherstellen? Pre-restore-Snapshot wird automatisch angelegt.")) return;
      const r = await api(`/api/projects/${slug}/snapshots/${s.id}/restore`, "POST", {});
      if (r.status !== 200) alert("Restore fehlgeschlagen");
      await renderHistory(body);
    });
    actions.appendChild(btnRestore);
    if (s.sql_dump_path) {
      const btnDump = document.createElement("button");
      btnDump.textContent = "SQL";
      btnDump.addEventListener("click", () => { window.location.href = `/api/projects/${slug}/snapshots/${s.id}/dump`; });
      actions.appendChild(btnDump);
    }
    const btnKeep = document.createElement("button");
    btnKeep.textContent = s.keep_forever ? "Keep: aus" : "Keep: ein";
    btnKeep.addEventListener("click", async () => {
      await api(`/api/projects/${slug}/snapshots/${s.id}`, "PATCH", { keepForever: !s.keep_forever });
      await renderHistory(body);
    });
    actions.appendChild(btnKeep);
    const btnDel = document.createElement("button");
    btnDel.textContent = "Löschen";
    btnDel.disabled = !!s.keep_forever;
    btnDel.addEventListener("click", async () => {
      if (!confirm("Snapshot löschen?")) return;
      const r = await api(`/api/projects/${slug}/snapshots/${s.id}`, "DELETE");
      if (r.status !== 204) alert("Löschen fehlgeschlagen");
      await renderHistory(body);
    });
    actions.appendChild(btnDel);
    body.appendChild(tr);
  }
}

function gather(root: HTMLElement, names: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const n of names) {
    const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${n}"]`);
    out[n] = el?.value ?? "";
  }
  return out;
}

function setupViewportAndSplitter(): void {
  const VIEWPORT_KEY = "stage-app:viewport";
  const SPLIT_BOTTOM_KEY = "stage-app:split-bottom";
  const SPLIT_RIGHT_KEY = "stage-app:split-right";
  const FRAME_WIDTH_KEYS: Record<"tablet" | "mobile", string> = {
    tablet: "stage-app:frame-width:tablet",
    mobile: "stage-app:frame-width:mobile",
  };
  const FRAME_DEFAULTS: Record<"tablet" | "mobile", number> = { tablet: 768, mobile: 375 };

  const wrapper = document.querySelector(".iframe-viewport") as HTMLElement | null;
  if (!wrapper) return;
  const buttons: Record<string, HTMLButtonElement | null> = {
    desktop: document.getElementById("viewport-desktop") as HTMLButtonElement | null,
    tablet: document.getElementById("viewport-tablet") as HTMLButtonElement | null,
    mobile: document.getElementById("viewport-mobile") as HTMLButtonElement | null,
  };

  const getStoredFrameWidth = (mode: "tablet" | "mobile"): number => {
    try {
      const raw = localStorage.getItem(FRAME_WIDTH_KEYS[mode]);
      const n = raw ? Number(raw) : NaN;
      return Number.isFinite(n) && n >= 200 && n <= 4000 ? n : FRAME_DEFAULTS[mode];
    } catch { return FRAME_DEFAULTS[mode]; }
  };

  const applyFrameWidthForMode = (mode: string): void => {
    if (mode === "tablet" || mode === "mobile") {
      const w = getStoredFrameWidth(mode);
      document.documentElement.style.setProperty("--frame-width", `${w}px`);
    } else {
      document.documentElement.style.removeProperty("--frame-width");
    }
  };

  const applyViewport = (mode: string): void => {
    const valid = mode === "tablet" || mode === "mobile" ? mode : "desktop";
    wrapper.setAttribute("data-viewport", valid);
    for (const [k, btn] of Object.entries(buttons)) {
      if (btn) btn.classList.toggle("active", k === valid);
    }
    applyFrameWidthForMode(valid);
    try { localStorage.setItem(VIEWPORT_KEY, valid); } catch { /* ignore */ }
  };

  const initialVp = (() => {
    try { return localStorage.getItem(VIEWPORT_KEY) ?? "desktop"; } catch { return "desktop"; }
  })();
  applyViewport(initialVp);

  for (const [mode, btn] of Object.entries(buttons)) {
    btn?.addEventListener("click", () => applyViewport(mode));
  }

  // Frame resize handle: only active in tablet/mobile. Drag right-edge to
  // grow/shrink the iframe width; persisted per mode so each mode keeps
  // its own preferred width.
  const frameHandle = document.getElementById("frame-resize-handle") as HTMLElement | null;
  const frameStage = document.getElementById("frame-stage") as HTMLElement | null;
  if (frameHandle && frameStage) {
    frameHandle.addEventListener("pointerdown", (ev: PointerEvent) => {
      const mode = wrapper.getAttribute("data-viewport");
      if (mode !== "tablet" && mode !== "mobile") return;
      ev.preventDefault();
      try { frameHandle.setPointerCapture(ev.pointerId); } catch { /* */ }
      frameHandle.classList.add("dragging");
      const startX = ev.clientX;
      const startW = frameStage.getBoundingClientRect().width;
      const onMove = (mv: PointerEvent): void => {
        const maxW = wrapper.getBoundingClientRect().width;
        const newW = Math.max(200, Math.min(maxW, startW + (mv.clientX - startX)));
        document.documentElement.style.setProperty("--frame-width", `${Math.round(newW)}px`);
      };
      const onEnd = (mv: PointerEvent): void => {
        frameHandle.classList.remove("dragging");
        try { frameHandle.releasePointerCapture(mv.pointerId); } catch { /* */ }
        frameHandle.removeEventListener("pointermove", onMove);
        frameHandle.removeEventListener("pointerup", onEnd);
        frameHandle.removeEventListener("pointercancel", onEnd);
        const finalW = Math.round(frameStage.getBoundingClientRect().width);
        try { localStorage.setItem(FRAME_WIDTH_KEYS[mode as "tablet" | "mobile"], String(finalW)); } catch { /* */ }
      };
      frameHandle.addEventListener("pointermove", onMove);
      frameHandle.addEventListener("pointerup", onEnd);
      frameHandle.addEventListener("pointercancel", onEnd);
    });
  }

  // Live pixel-width readout — observe the iframe and reflect actual rendered
  // width. Works for desktop (changes with split) and tablet/mobile (changes
  // with handle drag).
  const readout = document.getElementById("frame-width-readout") as HTMLElement | null;
  const stagingFrame = document.getElementById("staging-frame") as HTMLElement | null;
  if (readout && stagingFrame && typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.round(e.contentRect.width);
        readout.textContent = `${w} px`;
      }
    });
    ro.observe(stagingFrame);
  }

  const getStoredPct = (layoutMode: "bottom" | "right"): number => {
    const key = layoutMode === "bottom" ? SPLIT_BOTTOM_KEY : SPLIT_RIGHT_KEY;
    try {
      const raw = localStorage.getItem(key);
      const n = raw ? Number(raw) : NaN;
      return Number.isFinite(n) && n >= 10 && n <= 90 ? n : 60;
    } catch { return 60; }
  };

  const applySplitForCurrentLayout = (): void => {
    const mode = document.body.dataset["terminalPosition"] === "bottom" ? "bottom" : "right";
    const pct = getStoredPct(mode);
    document.documentElement.style.setProperty("--iframe-pct", String(pct));
  };
  applySplitForCurrentLayout();

  // Re-apply when the layout-toggle switches modes (it sets body's
  // data-terminal-position attribute). MutationObserver since we don't
  // own that code.
  const bodyObserver = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.attributeName === "data-terminal-position") {
        applySplitForCurrentLayout();
        break;
      }
    }
  });
  bodyObserver.observe(document.body, { attributes: true, attributeFilter: ["data-terminal-position"] });

  const splitterV = document.getElementById("splitter-vertical") as HTMLElement | null;
  const splitterH = document.getElementById("splitter-horizontal") as HTMLElement | null;

  const startDrag = (splitter: HTMLElement, axis: "x" | "y") => (ev: PointerEvent): void => {
    ev.preventDefault();
    splitter.classList.add("dragging");
    splitter.setPointerCapture(ev.pointerId);
    const main = document.querySelector(".app-main") as HTMLElement;
    const rect = main.getBoundingClientRect();
    const layoutMode = document.body.dataset["terminalPosition"] === "bottom" ? "bottom" : "right";

    const onMove = (mv: PointerEvent): void => {
      const pos = axis === "x"
        ? ((mv.clientX - rect.left) / rect.width) * 100
        : ((mv.clientY - rect.top) / rect.height) * 100;
      const clamped = Math.max(10, Math.min(90, pos));
      document.documentElement.style.setProperty("--iframe-pct", String(clamped));
    };
    const onEnd = (mv: PointerEvent): void => {
      splitter.classList.remove("dragging");
      try { splitter.releasePointerCapture(mv.pointerId); } catch { /* */ }
      splitter.removeEventListener("pointermove", onMove);
      splitter.removeEventListener("pointerup", onEnd);
      splitter.removeEventListener("pointercancel", onEnd);
      const finalPct = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--iframe-pct")) || 60;
      const key = layoutMode === "bottom" ? SPLIT_BOTTOM_KEY : SPLIT_RIGHT_KEY;
      try { localStorage.setItem(key, String(finalPct)); } catch { /* ignore */ }
    };
    splitter.addEventListener("pointermove", onMove);
    splitter.addEventListener("pointerup", onEnd);
    splitter.addEventListener("pointercancel", onEnd);
  };

  splitterV?.addEventListener("pointerdown", startDrag(splitterV, "x"));
  splitterH?.addEventListener("pointerdown", startDrag(splitterH, "y"));
}

function setMsg(el: HTMLElement | null, text: string, kind: "success" | "error" | ""): void {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("success", "error");
  if (kind) el.classList.add(kind);
}

async function openProjectForm(editSlug: string | null, onSaved: () => Promise<void>): Promise<void> {
  const m = openModal("tpl-project-form-modal");
  const titleEl = m.querySelector("[data-title]") as HTMLElement;
  const msgEl = m.querySelector('[data-msg="form"]') as HTMLElement;
  const submit = m.querySelector("[data-submit]") as HTMLButtonElement;

  if (editSlug) {
    titleEl.textContent = `Projekt bearbeiten: ${editSlug}`;
    const r = await api<ProjectFullResponse>(`/api/projects/${editSlug}`);
    if (r.status !== 200 || !r.data) { setMsg(msgEl, "Projekt nicht gefunden", "error"); return; }
    const p = r.data;
    const slugInput = m.querySelector('[name="slug"]') as HTMLInputElement;
    slugInput.value = p.slug; slugInput.disabled = true;
    (m.querySelector('[name="name"]') as HTMLInputElement).value = p.name;
    (m.querySelector('[name="stagingRootPath"]') as HTMLInputElement).value = p.stagingRootPath;
    (m.querySelector('[name="stagingUrl"]') as HTMLInputElement).value = p.stagingUrl;
    (m.querySelector('[name="stagingDbName"]') as HTMLInputElement).value = p.stagingDbName;
    (m.querySelector('[name="stagingDbUser"]') as HTMLInputElement).value = p.stagingDbUser;
    (m.querySelector('[name="stagingDbPassword"]') as HTMLInputElement).value = p.stagingDbPassword ?? "";
    (m.querySelector('[name="stagingBasicAuthUser"]') as HTMLInputElement).value = p.stagingBasicAuthUser;
    (m.querySelector('[name="stagingBasicAuthPassword"]') as HTMLInputElement).value = p.stagingBasicAuthPassword ?? "";
    (m.querySelector('[name="liveUrl"]') as HTMLInputElement).value = p.liveUrl;
    (m.querySelector('[name="liveSshHost"]') as HTMLInputElement).value = p.liveSshHost;
    (m.querySelector('[name="liveSshPort"]') as HTMLInputElement).value = String(p.liveSshPort);
    (m.querySelector('[name="liveSshUser"]') as HTMLInputElement).value = p.liveSshUser;
    (m.querySelector('[name="liveSshPrivateKey"]') as HTMLTextAreaElement).value = p.liveSshPrivateKey ?? "";
    (m.querySelector('[name="liveJoomlaPath"]') as HTMLInputElement).value = p.liveJoomlaPath;
    (m.querySelector('[name="liveDbHost"]') as HTMLInputElement).value = p.liveDbHost;
    (m.querySelector('[name="liveDbPort"]') as HTMLInputElement).value = String(p.liveDbPort);
    (m.querySelector('[name="liveDbUser"]') as HTMLInputElement).value = p.liveDbUser;
    (m.querySelector('[name="liveDbPassword"]') as HTMLInputElement).value = p.liveDbPassword ?? "";
    (m.querySelector('[name="liveDbName"]') as HTMLInputElement).value = p.liveDbName;
    (m.querySelector('[name="anthropicApiKey"]') as HTMLInputElement).value = p.anthropicApiKey ?? "";
    (m.querySelector('[name="snapshotRetention"]') as HTMLInputElement).value = String(p.snapshotRetention);
  } else {
    titleEl.textContent = "Neues Projekt";
  }

  // Auto-save draft for NEW project mode only — edit mode loads from server,
  // and saving draft for edit mode would mask server changes.
  const DRAFT_KEY = "stage-app:project-draft:new";
  const DRAFT_FIELDS = [
    "slug","name","stagingRootPath","stagingUrl",
    "stagingDbName","stagingDbUser","stagingDbPassword",
    "stagingBasicAuthUser","stagingBasicAuthPassword",
    "liveUrl","liveSshHost","liveSshPort","liveSshUser","liveSshPrivateKey","liveJoomlaPath",
    "liveDbHost","liveDbPort","liveDbUser","liveDbPassword","liveDbName",
    "anthropicApiKey","snapshotRetention",
  ];
  const draftMsgEl = m.querySelector('[data-msg="draft"]') as HTMLElement | null;
  if (!editSlug) {
    // Restore draft if present
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw) as Record<string, string>;
        let restored = 0;
        for (const f of DRAFT_FIELDS) {
          const el = m.querySelector(`[name="${f}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
          if (el && typeof draft[f] === "string" && draft[f] !== "") {
            el.value = draft[f]!;
            restored++;
          }
        }
        if (restored > 0 && draftMsgEl) {
          setMsg(draftMsgEl, `Entwurf wiederhergestellt (${restored} Felder). Auf "Speichern" → wenn OK wird der Entwurf verworfen.`, "success");
        }
      }
    } catch { /* ignore corrupt draft */ }

    // Save on every input — debounced to avoid hammering localStorage
    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    const saveDraft = (): void => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        const snap: Record<string, string> = {};
        for (const f of DRAFT_FIELDS) {
          const el = m.querySelector(`[name="${f}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
          if (el) snap[f] = el.value;
        }
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify(snap)); } catch { /* quota or disabled */ }
      }, 300);
    };
    for (const f of DRAFT_FIELDS) {
      const el = m.querySelector(`[name="${f}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
      if (el) el.addEventListener("input", saveDraft);
    }
  }

  const factsPreview = m.querySelector('textarea[name="claudeFactsPreview"]') as HTMLTextAreaElement;
  const notesInput = m.querySelector('textarea[name="claudeNotes"]') as HTMLTextAreaElement;
  const refreshBtn = m.querySelector('[data-action="refresh-facts"]') as HTMLButtonElement;
  const refreshMsg = m.querySelector('[data-msg="claude-facts"]') as HTMLElement;

  if (editSlug) {
    // Editing an existing project — load Claude context
    refreshBtn.disabled = false;
    void (async () => {
      const r = await api<{ rules?: string; facts?: string; notes?: string; factsRegeneratedAt?: string | null }>(
        `/api/projects/${editSlug}/claude`, "GET",
      );
      if (r.status === 200 && r.data) {
        factsPreview.value = r.data.facts ?? "";
        notesInput.value = r.data.notes ?? "";
      }
    })();

    refreshBtn.addEventListener("click", async () => {
      setMsg(refreshMsg, "Aktualisiere …", "");
      refreshBtn.disabled = true;
      const r = await api<{ facts?: string; regeneratedAt?: string; error?: string }>(
        `/api/projects/${editSlug}/claude/refresh-facts`, "POST", {},
      );
      refreshBtn.disabled = false;
      if (r.status === 200 && r.data?.facts !== undefined) {
        factsPreview.value = r.data.facts;
        setMsg(refreshMsg, `Aktualisiert ${r.data.regeneratedAt ?? ""}`, "success");
      } else {
        setMsg(refreshMsg, "Fehler: " + (r.data?.error ?? String(r.status)), "error");
      }
    });
  }

  submit.addEventListener("click", async () => {
    submit.disabled = true; setMsg(msgEl, "Speichern …", "");
    const v = gather(m, [
      "slug","name","stagingRootPath","stagingUrl",
      "stagingDbName","stagingDbUser","stagingDbPassword",
      "stagingBasicAuthUser","stagingBasicAuthPassword",
      "liveUrl","liveSshHost","liveSshPort","liveSshUser","liveSshPrivateKey","liveJoomlaPath",
      "liveDbHost","liveDbPort","liveDbUser","liveDbPassword","liveDbName",
      "anthropicApiKey","snapshotRetention",
    ]);
    const payload = {
      ...(editSlug ? {} : { slug: v["slug"] }),
      name: v["name"],
      stagingRootPath: v["stagingRootPath"],
      stagingUrl: v["stagingUrl"],
      stagingDbName: v["stagingDbName"],
      stagingDbUser: v["stagingDbUser"],
      stagingDbPassword: v["stagingDbPassword"] ?? "",
      stagingBasicAuthUser: v["stagingBasicAuthUser"],
      stagingBasicAuthPassword: v["stagingBasicAuthPassword"] ?? "",
      liveUrl: v["liveUrl"],
      liveSshHost: v["liveSshHost"],
      liveSshPort: Number.parseInt(v["liveSshPort"] ?? "22", 10) || 22,
      liveSshUser: v["liveSshUser"],
      liveSshPrivateKey: v["liveSshPrivateKey"] ?? "",
      liveJoomlaPath: v["liveJoomlaPath"],
      liveDbHost: v["liveDbHost"],
      liveDbPort: Number.parseInt(v["liveDbPort"] ?? "3306", 10) || 3306,
      liveDbUser: v["liveDbUser"],
      liveDbPassword: v["liveDbPassword"] ?? "",
      liveDbName: v["liveDbName"],
      anthropicApiKey: v["anthropicApiKey"] ? v["anthropicApiKey"] : null,
      snapshotRetention: Number.parseInt(v["snapshotRetention"] ?? "20", 10) || 20,
    };

    const res = editSlug
      ? await api<{ ok?: boolean; error?: string; issues?: Array<{ path?: Array<string|number>; message?: string }> }>(`/api/projects/${editSlug}`, "PUT", payload)
      : await api<{ id?: number; error?: string; issues?: Array<{ path?: Array<string|number>; message?: string }> }>("/api/projects", "POST", payload);

    if (res.status === 200 || res.status === 201) {
      setMsg(msgEl, "Gespeichert.", "success");
      if (!editSlug) {
        // Clear the new-project draft now that it's persisted server-side
        try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      }
      if (editSlug) {
        // Persist notes alongside the project save
        const notesRes = await api<{ ok?: boolean; error?: string }>(
          `/api/projects/${editSlug}/claude/notes`, "PATCH",
          { markdown: notesInput.value },
        );
        if (notesRes.status !== 200) {
          // Don't fail the whole save — notes saving is secondary
          console.warn("notes save failed:", notesRes.data?.error ?? notesRes.status);
        }
      }
      await onSaved();
      setTimeout(() => m.remove(), 600);
    } else {
      // Build a human-readable error including per-field issues if present
      let msg = "Fehler: " + (res.data?.error ?? String(res.status));
      const issues = res.data?.issues;
      if (Array.isArray(issues) && issues.length > 0) {
        const items = issues.map((iss) => {
          const path = Array.isArray(iss.path) && iss.path.length > 0 ? iss.path.join(".") : "(unknown)";
          return `${path}: ${iss.message ?? "?"}`;
        });
        msg += " — " + items.join(" • ");
      }
      setMsg(msgEl, msg, "error");
      submit.disabled = false;
    }
  });
}

async function renderProjectsList(body: HTMLElement, reload: () => Promise<void>): Promise<void> {
  body.innerHTML = "";
  const r = await api<ProjectsResponse>("/api/projects");
  for (const p of r.data?.projects ?? []) {
    const tr = document.createElement("tr");
    tr.appendChild(td(p.slug));
    tr.appendChild(td(p.name));
    tr.appendChild(td(p.stagingUrl));
    const actions = document.createElement("td");
    tr.appendChild(actions);
    const btnEdit = document.createElement("button");
    btnEdit.textContent = "Bearbeiten";
    btnEdit.addEventListener("click", () => {
      void openProjectForm(p.slug, reload);
    });
    actions.appendChild(btnEdit);
    const btnDel = document.createElement("button");
    btnDel.textContent = "Löschen";
    btnDel.addEventListener("click", async () => {
      if (!confirm(`Projekt '${p.slug}' löschen?`)) return;
      const d = await api(`/api/projects/${p.slug}`, "DELETE");
      if (d.status !== 204) alert("Löschen fehlgeschlagen");
      await reload();
    });
    actions.appendChild(btnDel);
    body.appendChild(tr);
  }
}


// ---- Dateiaustausch ------------------------------------------------------
function fmtBytes(n: number): string {
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / 1024 / 1024).toFixed(1) + " MB";
}


function openConfigModal(reloadProjects: () => Promise<void>): void {
  const m = openModal("tpl-config-modal");

  m.querySelectorAll<HTMLButtonElement>(".tab-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      m.querySelectorAll(".tab-btn").forEach((b) => b.classList.remove("active"));
      m.querySelectorAll(".tab-panel").forEach((p) => p.classList.remove("active"));
      btn.classList.add("active");
      const panel = m.querySelector(`[data-tab-panel="${btn.dataset.tab}"]`);
      panel?.classList.add("active");
    });
  });

  const pwMsg = m.querySelector('[data-msg="password"]') as HTMLElement;
  (m.querySelector('[data-action="change-password"]') as HTMLButtonElement).addEventListener("click", async () => {
    const cur = (m.querySelector('[name="currentPassword"]') as HTMLInputElement).value;
    const nw = (m.querySelector('[name="newPassword"]') as HTMLInputElement).value;
    setMsg(pwMsg, "…", "");
    const r = await api<{ error?: string }>("/api/me/password", "PATCH", { currentPassword: cur, newPassword: nw });
    if (r.status === 200) setMsg(pwMsg, "Passwort geändert.", "success");
    else if (r.status === 401) setMsg(pwMsg, "Aktuelles Passwort falsch.", "error");
    else if (r.status === 400) setMsg(pwMsg, "Neues Passwort muss mind. 10 Zeichen haben.", "error");
    else setMsg(pwMsg, "Fehler: " + (r.data?.error ?? String(r.status)), "error");
  });

  const projectsBody = m.querySelector("#projects-list-body") as HTMLElement;
  const reloadList = async (): Promise<void> => {
    await renderProjectsList(projectsBody, reloadList);
    await reloadProjects();
  };
  void renderProjectsList(projectsBody, reloadList);
  (m.querySelector('[data-action="new-project"]') as HTMLButtonElement).addEventListener("click", () => {
    void openWizard({
      api,
      openModal,
      setMsg,
      onSaved: reloadList,
    });
  });
}

async function initLogin(form: HTMLFormElement): Promise<void> {
  const err = document.getElementById("login-error") as HTMLElement;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    err.hidden = true;
    const fd = new FormData(form);
    const res = await api<{ ok: boolean }>(
      "/login", "POST", { username: fd.get("username"), password: fd.get("password") },
    );
    if (res.status === 200) location.href = "/app";
    else if (res.status === 429) { err.textContent = "Zu viele Fehlversuche. Bitte später erneut."; err.hidden = false; }
    else { err.textContent = "Anmeldung fehlgeschlagen."; err.hidden = false; }
  });
}

/**
 * Frank-Liste (Uli 01.10.2026): Menüpunkt mit PDF-Symbol. Ein Klick öffnet immer die aktuelle Liste der offenen
 * Punkte für Frank (PDF, geht per Teams an ihn); die Zahl am Knopf = offene Punkte. Datenbasis data/frank_liste.json.
 */
function setupFrankListe(): void {
  const btn = document.getElementById("frank-btn") as HTMLButtonElement | null;
  const badge = document.getElementById("frank-badge") as HTMLElement | null;
  if (!btn) return;
  const lade = async (): Promise<void> => {
    const r = await api<{ ok: boolean; offen: number; stand: string | null }>("/api/frank-liste");
    if (!badge || !r.data?.ok) return;
    badge.textContent = String(r.data.offen);
    badge.hidden = r.data.offen === 0;
    if (r.data.stand) btn.title = `Frank-Liste: ${r.data.offen} offene Punkte · Stand ${formatDate(r.data.stand)} – als PDF zum Weiterschicken per Teams`;
  };
  btn.addEventListener("click", () => {
    window.open(`/api/frank-liste.pdf?t=${Date.now()}`, "_blank", "noopener");
    void lade();
  });
  void lade();
  window.setInterval(() => { void lade(); }, 15 * 60_000);
}

async function initApp(): Promise<void> {
  const me = await api<MeResponse>("/api/me");
  if (me.status !== 200 || !me.data) { location.href = "/login"; return; }
  (document.getElementById("user-name") as HTMLElement).textContent = me.data.username;

  const select = document.getElementById("project-select") as HTMLSelectElement;
  const frame = document.getElementById("staging-frame") as HTMLIFrameElement;
  const termHost = document.getElementById("terminal-host") as HTMLElement;
  const statusEl = document.getElementById("terminal-status") as HTMLElement;
  const claudeBtn = document.getElementById("claude-start-btn") as HTMLButtonElement;
  const clearBtn = document.getElementById("terminal-clear-btn") as HTMLButtonElement;
  const layoutBtn = document.getElementById("layout-toggle") as HTMLButtonElement;
  const logoutBtn = document.getElementById("logout-btn") as HTMLButtonElement;
  const snapBtn = document.getElementById("snap-btn") as HTMLButtonElement;
  const undoBtn = document.getElementById("undo-btn") as HTMLButtonElement;
  const historyBtn = document.getElementById("history-btn") as HTMLButtonElement;
  const configBtn = document.getElementById("config-btn") as HTMLButtonElement;
  const pullBtn = document.getElementById("pull-btn") as HTMLButtonElement;
  const frontendBtn = document.getElementById("frontend-btn") as HTMLButtonElement;
  const adminBtn = document.getElementById("admin-btn") as HTMLButtonElement;

  setupMarketing({ api, openModal, formatDate });
  setupWatchdog({ api, openModal, formatDate, currentSlug });
  setupWettbewerb({ api, formatDate });
  setupFrankListe();

  let controller: TerminalController | null = null;

  const applyProject = (): void => {
    const opt = select.selectedOptions[0];
    if (!opt?.dataset.url) {
      frame.src = "about:blank";
      frontendBtn.disabled = true;
      adminBtn.disabled = true;
      if (controller) { controller.dispose(); controller = null; }
      return;
    }
    frame.src = opt.dataset.url;
    frontendBtn.disabled = false;
    adminBtn.disabled = false;
    document.body.dataset.terminalPosition = opt.dataset.pos === "right" ? "right" : "bottom";
    if (controller) controller.dispose();
    controller = mountTerminal(termHost);
    controller.start(opt.value);
    statusEl.textContent = "● bash";
  };

  frontendBtn.addEventListener("click", () => {
    const opt = select.selectedOptions[0];
    if (!opt?.dataset.url) return;
    frame.src = opt.dataset.url;
  });

  adminBtn.addEventListener("click", () => {
    const opt = select.selectedOptions[0];
    if (!opt?.dataset.url) return;
    const base = opt.dataset.url.replace(/\/$/, "");
    frame.src = `${base}/administrator`;
  });

  const ACTIVE_PROJECT_KEY = "stage-app:active-project";

  // --- Stage-Abgleich: welche Portale hinken hinter Live her? ---------------
  //
  // Gearbeitet wird ueberwiegend auf Live, die Stagings hinken hinterher. Der
  // naechtliche Lauf (claude_stage_drift.mjs) schreibt das Ergebnis nach
  // data/stage_drift_alert.json; hier wird es nur angezeigt. Kein eigener
  // Zugriff auf Live, kein Pull — das bleibt ein bewusster Klick.
  // DriftPortal + driftText sind Modul-Funktionen (oben) — der Sammel-Pull-
  // Dialog (openPullAllModal) braucht dieselbe Form ausserhalb dieser Closure.
  let driftStand: { geprueft: string | null; portale: DriftPortal[] } = { geprueft: null, portale: [] };

  const driftFlag = document.getElementById("drift-flag") as HTMLButtonElement | null;
  const pullAllBtn = document.getElementById("pull-all-btn") as HTMLButtonElement | null;
  const pullSperreBtn = document.getElementById("pull-sperre-btn") as HTMLButtonElement | null;

  // Schloss neben dem Umschalter: zeigt, ob das gewählte Portal gegen Pulls gesperrt ist (siehe pullSperren).
  const zeigePullSperre = (): void => {
    if (!pullSperreBtn) return;
    const s = pullSperren[select.value];
    pullSperreBtn.hidden = !select.value;
    pullSperreBtn.classList.toggle("pull-sperre-btn--an", !!s);
    pullSperreBtn.textContent = s ? "🔒 Pull gesperrt" : "🔓";
    pullSperreBtn.title = s
      ? `Pull gesperrt seit ${formatDate(s.seit)}: ${s.grund}\nKlicken zum Lösen.`
      : "Pull-Sperre setzen (Staging in Arbeit — kein Pull überschreibt es)";
  };

  const zeigeDriftFlagge = (): void => {
    if (!driftFlag) return;
    const slug = select.value;
    const d = driftStand.portale.find((x) => x.slug === slug);

    if (!d || (!d.pullNoetig && d.startseiteOk && !d.unbekannt)) {
      driftFlag.hidden = true;
      return;
    }
    // Gesperrtes Portal: „Pull nötig“ ist bewusst ausgesetzt — das Schloss zeigt den Stand. Eine defekte
    // Startseite bleibt trotzdem sichtbar (das ist kein Pull-Thema).
    if (pullSperren[slug] && d.startseiteOk) {
      driftFlag.hidden = true;
      return;
    }

    driftFlag.hidden = false;
    driftFlag.classList.toggle("drift-flag--defekt", !d.startseiteOk);
    driftFlag.classList.toggle("drift-flag--unklar", !!d.unbekannt && d.startseiteOk && !d.pullNoetig);

    const geprueft = driftStand.geprueft
      ? new Date(driftStand.geprueft).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })
      : "unbekannt";

    if (!d.startseiteOk) {
      driftFlag.textContent = "⛔ Startseite defekt";
      driftFlag.title = `${d.hinweise.join("\n")}\n\nGeprüft: ${geprueft}`;
    } else if (d.unbekannt && !d.pullNoetig) {
      // Kein Ergebnis ist keine Entwarnung.
      driftFlag.textContent = "❔ Abgleich fehlgeschlagen";
      driftFlag.title =
        `Der nächtliche Abgleich konnte für dieses Portal nichts ermitteln — ` +
        `ob Live neuer ist, ist damit offen.\n${d.hinweise.join("\n")}\n\nGeprüft: ${geprueft}`;
    } else {
      driftFlag.textContent = `🚩 Pull nötig`;
      driftFlag.title =
        `Live ist neuer als dieses Staging: ${driftText(d)}.\n` +
        `Letzter Pull: ${d.letzterPull ?? "nie"}\n` +
        (d.hinweise.length ? `${d.hinweise.join("\n")}\n` : "") +
        `\nGeprüft: ${geprueft}\nKlicken öffnet den Pull-Dialog.`;
    }
  };

  // Knopf neben der Drift-Fahne: zieht alle faelligen Portale nacheinander
  // nach, statt sie einzeln von Hand anzuklicken. "Faellig" = pullNoetig
  // (Live ist neuer) — ein rein defektes Portal (startseiteOk=false ohne
  // pullNoetig) wuerde ein Pull nicht reparieren, das gehoert nicht in die
  // Zaehlung. Versteckt, solange nichts anliegt (siehe .drift-flag oben).
  const zeigePullAllBtn = (): void => {
    if (!pullAllBtn) return;
    const anzahl = driftStand.portale.filter((d) => d.pullNoetig && !pullSperren[d.slug]).length;
    if (anzahl === 0) {
      pullAllBtn.hidden = true;
      return;
    }
    pullAllBtn.hidden = false;
    pullAllBtn.textContent = `⬇⬇ Portale auswählen (${anzahl})`;
    pullAllBtn.title =
      `${anzahl} Portal${anzahl === 1 ? " liegt" : "e liegen"} hinter Live zurück. Prüft frisch nach; danach wählen Sie per Haken, ` +
      `welche Portale gezogen werden. Deren Staging-Stände werden dabei überschrieben.`;
  };

  const ladeDrift = async (): Promise<void> => {
    await ladePullSperren();
    const r = await api<{ geprueft: string | null; portale: DriftPortal[] }>("/api/stage-drift");
    driftStand = { geprueft: r.data?.geprueft ?? null, portale: r.data?.portale ?? [] };

    // Betroffene Portale auch im aufgeklappten Umschalter kennzeichnen.
    for (const opt of Array.from(select.options)) {
      const d = driftStand.portale.find((x) => x.slug === opt.value);
      const markiert = !!d && (d.pullNoetig || !d.startseiteOk || !!d.unbekannt);
      opt.classList.toggle("drift-option", markiert);
      const roh = opt.dataset.name ?? opt.textContent ?? "";
      const zeichen = !d ? "" : !d.startseiteOk ? "⛔" : d.pullNoetig ? "🚩" : "❔";
      // Gesperrt: nur das Schloss zeigen — die rote Pull-Fahne daneben wäre ein Widerspruch (Pull ist ja gewollt aus).
      // Eine defekte Startseite (⛔) bleibt sichtbar, das ist kein Pull-Thema.
      const gesperrt = !!pullSperren[opt.value];
      const zeigeZeichen = markiert && !(gesperrt && d?.startseiteOk);
      opt.textContent = `${gesperrt ? "🔒 " : ""}${zeigeZeichen ? `${zeichen} ` : ""}${roh}`;
    }
    zeigeDriftFlagge();
    zeigePullAllBtn();
    zeigePullSperre();
  };

  pullAllBtn?.addEventListener("click", () => {
    // Waehrend ein Dialog offen ist, deaktiviert — sonst oeffnet ein
    // Doppelklick zwei uebereinanderliegende Dialoge, jeder mit eigener
    // Socket-Verbindung; der zweite landet nur sofort im 409-Zweig
    // (sammellauf_aktiv). Ungefaehrlich, aber verwirrend. openPullAllModal()
    // faengt alle eigenen Fehler intern ab (siehe try/catch dort) und
    // resolved daher praktisch immer — .finally() reicht als Gegenstueck.
    pullAllBtn.disabled = true;
    void openPullAllModal().finally(() => { pullAllBtn.disabled = false; });
  });

  driftFlag?.addEventListener("click", () => {
    // Doppelte Absicherung zur Server-Sperre: ein gesperrtes Portal öffnet statt des Pulls den Sperr-Dialog.
    if (pullSperren[select.value]) {
      openPullSperreModal(select.value, select.selectedOptions[0]?.dataset.name ?? select.value, () => { void ladeDrift(); });
      return;
    }
    (document.getElementById("pull-btn") as HTMLButtonElement | null)?.click();
  });

  pullSperreBtn?.addEventListener("click", () => {
    if (!select.value) return;
    openPullSperreModal(select.value, select.selectedOptions[0]?.dataset.name ?? select.value, () => { void ladeDrift(); });
  });

  const loadProjects = async (): Promise<void> => {
    const p = await api<ProjectsResponse>("/api/projects");
    const list = p.data?.projects ?? [];
    // Prefer the dropdown's current value (in case loadProjects is called
    // mid-session, e.g. after a project edit), else fall back to the
    // last-active slug from localStorage so a page reload keeps the user
    // on the project they were on.
    const prev = select.value || (() => {
      try { return localStorage.getItem(ACTIVE_PROJECT_KEY) ?? ""; } catch { return ""; }
    })();
    select.innerHTML = "";
    for (const pr of list) {
      const opt = document.createElement("option");
      opt.value = pr.slug; opt.textContent = pr.name;
      // Originalname merken, damit die Drift-Markierung sich beim Neuladen
      // nicht selbst nochmal davorsetzt.
      opt.dataset.name = pr.name;
      opt.dataset.url = pr.stagingUrl; opt.dataset.pos = pr.uiTerminalPosition;
      select.appendChild(opt);
    }
    if (prev && list.some((pr) => pr.slug === prev)) select.value = prev;
    applyProject();
    void ladeDrift();
  };

  await loadProjects();
  select.addEventListener("change", () => {
    try { localStorage.setItem(ACTIVE_PROJECT_KEY, select.value); } catch { /* ignore */ }
    applyProject();
    zeigeDriftFlagge();
    zeigePullSperre();
  });

  // Nach einem Pull streicht der Server die Markierung fuer dieses Portal
  // (src/sync/drift-flag.ts) — hier nur noch nachladen.
  window.addEventListener("stage-app:pull-done", () => { void ladeDrift(); });

  // --- Waechter-Warnband ---------------------------------------------------
  //
  // Sammelband fuer alle Hintergrund-Waechter (stage-drift, fernusg, …).
  // Systemweit, deshalb ein Band unter dem Kopf statt einer Fahne am Portal.
  // Per Mail ginge es nicht: von dieser Maschine ist kein zustellbarer Versand
  // moeglich (siehe stage_app_warnungen.mjs).
  //
  // Zwei Stufen: 'ausfall' ist NICHT wegklickbar — ein stiller Ausfall der
  // Ueberwachung saehe sonst aus wie Ruhe. 'fund' schon, denn ein Fund loest
  // sich nicht von selbst auf und bliebe sonst ewig stehen.
  interface WatchdogWarnung {
    quelle: string; stufe: "ausfall" | "fund"; titel: string;
    probleme: string[]; gemeldet: string; details?: string[]; nachholen?: string;
  }
  const band = document.getElementById("watchdog-band") as HTMLElement | null;

  const zeichneWarnung = (w: WatchdogWarnung): HTMLElement => {
    const box = document.createElement("div");
    box.className = `wb-eintrag wb-${w.stufe}`;

    const titel = document.createElement("div");
    titel.className = "wb-titel";
    titel.textContent = `${w.stufe === "fund" ? "🔎" : "⚠"} ${w.titel}`;
    box.appendChild(titel);

    if (w.probleme.length) {
      const liste = document.createElement("ul");
      for (const p of w.probleme) {
        const li = document.createElement("li");
        li.textContent = p;
        liste.appendChild(li);
      }
      box.appendChild(liste);
    }

    const fuss = document.createElement("div");
    fuss.className = "wb-fuss";
    fuss.append(`Gemeldet: ${new Date(w.gemeldet).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}`);
    if (w.nachholen) {
      fuss.append(" · Nachholen: ");
      const code = document.createElement("code");
      code.textContent = w.nachholen;
      fuss.appendChild(code);
    }
    box.appendChild(fuss);

    if (w.stufe === "fund") {
      const ack = document.createElement("button");
      ack.className = "wb-ack";
      ack.textContent = "Gesehen";
      ack.addEventListener("click", async () => {
        ack.disabled = true;
        const r = await api(`/api/watchdog-alert/${encodeURIComponent(w.quelle)}/ack`, "POST", {});
        if (r.status !== 200) {
          ack.disabled = false;
          ack.textContent = "Fehlgeschlagen — nochmal";
          return;
        }
        void ladeWaechter();
      });
      box.appendChild(ack);
    }

    // Die Detailzeilen nur im Tooltip — im Band wuerden sie den Kopf sprengen.
    if (w.details?.length) box.title = w.details.join("\n");
    return box;
  };

  const ladeWaechter = async (): Promise<void> => {
    if (!band) return;
    const r = await api<{ ok: boolean; warnungen: WatchdogWarnung[] }>("/api/watchdog-alert");
    const warnungen = r.data?.warnungen ?? [];
    const unlesbar = r.data?.ok === false;

    if (!warnungen.length && !unlesbar) {
      band.hidden = true;
      return;
    }

    band.hidden = false;
    band.replaceChildren();
    for (const w of warnungen) band.appendChild(zeichneWarnung(w));

    // ok=false heisst: mindestens eine Warndatei ist unlesbar. Das ist keine
    // Entwarnung und muss sichtbar bleiben, auch neben lesbaren Warnungen.
    if (unlesbar) {
      const hinweis = document.createElement("div");
      hinweis.className = "wb-eintrag wb-ausfall";
      hinweis.textContent = "⚠ Mindestens eine Wächter-Warnung ist unlesbar — Zustand insoweit unbekannt.";
      band.appendChild(hinweis);
    }
  };

  void ladeWaechter();
  // Die Waechter laufen taeglich bzw. woechentlich; stuendlich nachsehen genuegt.
  setInterval(() => { void ladeWaechter(); }, 60 * 60 * 1000);

  // Der Nachtlauf schreibt einmal taeglich; ein Blick pro Viertelstunde genuegt
  // als Auffangnetz, falls der Pull-Abschluss mal nicht ankommt.
  setInterval(() => { void ladeDrift(); }, 15 * 60 * 1000);
  setupViewportAndSplitter();
  setupScreenshotButton(currentSlug);

  claudeBtn.addEventListener("click", async () => {
    if (!controller) return;
    const slug = currentSlug();
    if (!slug) return;

    // Smart pre-check: don't double-launch if Claude is already running.
    const cmdRes = await api<{ command: string | null }>(
      `/api/projects/${slug}/terminal/pane-cmd`, "GET",
    );
    if (cmdRes.status === 200 && cmdRes.data?.command === "claude") {
      // Already running — page is already attached via tmux. No-op + brief notice.
      statusEl.textContent = "● claude (bereits aktiv)";
      setTimeout(() => { statusEl.textContent = "● claude"; }, 2000);
      return;
    }

    // Refresh live-state for the upcoming Claude session.
    const r = await api<{ ok?: boolean; error?: string }>(
      `/api/projects/${slug}/claude/state`, "POST", {},
    );
    if (r.status !== 200) {
      console.warn("state refresh failed:", r.data?.error ?? r.status);
    }
    controller.sendCommand("claude --dangerously-skip-permissions");
    statusEl.textContent = "● claude";
  });

  const resetBtn = document.getElementById("terminal-reset-btn") as HTMLButtonElement | null;
  resetBtn?.addEventListener("click", async () => {
    const slug = currentSlug();
    if (!slug) return;
    if (!confirm("Terminal-Session beenden? Laufende Prozesse (z.B. Claude) werden geschlossen.")) return;
    const r = await api<{ ok?: boolean; killed?: boolean }>(
      `/api/projects/${slug}/terminal`, "DELETE",
    );
    if (r.status === 200) {
      // Cleanest reattach path: full page reload re-creates a fresh tmux session
      // via the `-A` flag the next time the socket emits "start".
      location.reload();
    } else {
      alert("Reset fehlgeschlagen: " + String(r.status));
    }
  });

  clearBtn.addEventListener("click", () => { controller?.clear(); });

  layoutBtn.addEventListener("click", async () => {
    const current = document.body.dataset.terminalPosition === "right" ? "right" : "bottom";
    const next: "bottom" | "right" = current === "right" ? "bottom" : "right";
    document.body.dataset.terminalPosition = next;
    const slug = currentSlug();
    if (slug) await api(`/api/projects/${slug}/preferences`, "PATCH", { uiTerminalPosition: next });
  });

  logoutBtn.addEventListener("click", async () => {
    await api("/logout", "POST", {});
    location.href = "/login";
  });

  snapBtn.addEventListener("click", () => {
    const m = openModal("tpl-snap-modal");
    const labelInput = m.querySelector('input[name="label"]') as HTMLInputElement;
    const keepInput = m.querySelector('input[name="keep"]') as HTMLInputElement;
    const submitBtn = m.querySelector("[data-submit]") as HTMLButtonElement;
    submitBtn.addEventListener("click", async () => {
      submitBtn.disabled = true; submitBtn.textContent = "…";
      await doSnapshot(labelInput.value.trim(), keepInput.checked);
      m.remove();
    });
  });
  undoBtn.addEventListener("click", () => {
    const m = openModal("tpl-undo-modal");
    const submitBtn = m.querySelector("[data-submit]") as HTMLButtonElement;
    submitBtn.addEventListener("click", async () => {
      submitBtn.disabled = true; submitBtn.textContent = "…";
      await doUndo();
      m.remove();
    });
  });
  historyBtn.addEventListener("click", async () => {
    const m = openModal("tpl-history-modal");
    const body = m.querySelector("#history-body") as HTMLElement;
    await renderHistory(body);
  });
  configBtn.addEventListener("click", () => {
    openConfigModal(loadProjects);
  });
  pullBtn.addEventListener("click", () => { void openPullModal(); });
  document.getElementById("publish-btn")?.addEventListener("click", () => { void openPublishModal(); });
  document.getElementById("content-publish-btn")?.addEventListener("click", () => { void openContentPublishModal(); });
}

const loginForm = document.getElementById("login-form") as HTMLFormElement | null;
if (loginForm) { void initLogin(loginForm); }
else if (document.body.classList.contains("app-body")) { void initApp(); }
